import { DownloadOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { I18n } from '@aws-amplify/core';
import { Button, Col, Row } from 'antd';
import React from 'react';

import { useApplicationContext } from '@/context/v1/Application/context';
import { useSetConsolidateBeraSummary } from '@/hooks/useSetConsolidateBeraSummary';
import { useDownloadPDF } from '../hooks';

interface FooterProps {
	reportId: string;
	locale: string;
	status?: boolean;
	disabled?: boolean;
	consolidated?: boolean;
	onConsolidate: () => void;
}

const CONSOLIDATE_COLOR = '#17A93B';
const DECONSOLIDATE_COLOR = '#D13342';

export function Footer({
	reportId,
	locale,
	disabled = false,
	consolidated = true,
	onConsolidate
}: Readonly<FooterProps>): JSX.Element {
	const { organization, company } = useApplicationContext();
	const { isLoading: isLoadingDownloadPDF, mutateAsync: downloadPDF } = useDownloadPDF();
	const { isLoading: isLoadingConsolidateReport, mutateAsync: consolidateReport } = useSetConsolidateBeraSummary();

	async function handleDownloadOnClick(): Promise<void> {
		const payload = {
			organization_id: organization?.id!,
			company_id: company?.id!,
			bera_job_summary_id: reportId,
			locale
		};
		await downloadPDF(payload);
	}

	async function handleConsolidateOnClick(): Promise<void> {
		const payload = {
			id: reportId,
			organization_id: organization?.id!,
			company_id: company?.id!,
			locale,
			consolidated: !consolidated
		};
		await consolidateReport(payload);
		onConsolidate();
	}

	function renderConsolidateIcon(): JSX.Element {
		return consolidated ? (
			<UnlockOutlined style={{ color: DECONSOLIDATE_COLOR }} />
		) : (
			<LockOutlined style={{ color: CONSOLIDATE_COLOR }} />
		);
	}

	const isLoading = isLoadingConsolidateReport || isLoadingDownloadPDF;

	function getConsolidateText(): string {
		if (consolidated) {
			return I18n.get('Deconsolidate');
		}

		return I18n.get('Consolidate report');
	}

	function getConsolidateColor(): string {
		if (consolidated) {
			return DECONSOLIDATE_COLOR;
		}

		return CONSOLIDATE_COLOR;
	}

	return (
		<Col style={{ display: 'flex', justifyContent: 'flex-end' }} span={24}>
			<Row justify="end" style={{ gap: '1rem' }}>
				<Button
					style={{
						color: getConsolidateColor(),
						borderRadius: '0.25rem',
						border: `1px solid ${getConsolidateColor()}`
					}}
					loading={isLoading}
					disabled={isLoading || disabled}
					onClick={handleConsolidateOnClick}
					icon={renderConsolidateIcon()}
				>
					{getConsolidateText()}
				</Button>
				<Button
					style={{
						color: '#2F54EB',
						borderRadius: '0.25rem',
						border: '1px solid #2F54EB'
					}}
					loading={isLoading}
					disabled={isLoading || disabled}
					onClick={handleDownloadOnClick}
					icon={<DownloadOutlined style={{ color: '#2F54EB' }} />}
				>
					{I18n.get('Download PDF')}
				</Button>
			</Row>
		</Col>
	);
}
